import { HttpClient } from '@/api/services/http';
import { CopilotProfile } from '@/types/copilot-profile';
import { CopilotInitializeResponse } from '@/types/copilot';
import { getUserId, getWorkspaceId } from '@/utils/helpers/helpers';

const API_URL = '/copilot-profiles';

export interface CreateCopilotProfileRequest {
  name: string;
  persona_name: string;
  avatar_url: string;
  team_id: string;
  system_actions?: string[];
  custom_actions?: string[];
  external_actions?: string[];
  content_ids?: string[];
  action_ids?: string[];
}

export interface GetCopilotListResponse {
  data: CopilotProfile[];
}

export interface UserAccessibleCopilotsResponse {
  data: {
    copilots: CopilotProfile[];
    userTeams: any[]; // Will be typed properly when Team type is available
    totalAccessible: number;
  };
}

export interface CopilotAccessCheckResponse {
  data: {
    hasAccess: boolean;
    copilot?: CopilotProfile;
    reason?: string;
  };
}

export interface CreateConversationRequest {
  copilot_profile_id: string;
  page_url?: string;
  title?: string;
}

export interface Conversation {
  id: string;
  thread_id?: string;
  copilot_profile_id: string;
  user_id: string;
  workspace_id: string;
  team_id?: string | null;
  title: string;
  title_version?: number;
  status?: string;
  page_url: string | null;
  page_context: Record<string, any> | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  last_message_at?: string | null;
  message_count?: number;
  metadata?: Record<string, any>;
}

export interface StreamTokenResponse {
  success: boolean;
  stream_token: string | null;
  stream_token_expires_at?: string | null;
}

export interface CreateConversationResponse extends StreamTokenResponse {
  conversation: Conversation;
}

export interface ConversationMessage {
  id: string;
  conversation_id: string;
  role: string;
  content: string | null;
  metadata?: Record<string, any> | null;
  sequence_number: number;
  created_at: string;
  [key: string]: any;
}

export interface ConversationMessagesResponse {
  success: boolean;
  messages: ConversationMessage[];
  count: number;
}

export const copilotService = {
  async createCopilot(data: CreateCopilotProfileRequest): Promise<CopilotProfile> {
    const response = await HttpClient.apiClient.post(API_URL, data);
    return response.data;
  },

  async getCopilotList(): Promise<GetCopilotListResponse> {
    const response = await HttpClient.apiClient.get(API_URL);
    return response.data;
  },

  async getCopilot(id: string): Promise<CopilotProfile> {
    const response = await HttpClient.apiClient.get(`${API_URL}/${id}`);
    return response.data;
  },

  async updateCopilot(id: string, data: CreateCopilotProfileRequest): Promise<CopilotProfile> {
    const response = await HttpClient.apiClient.put(`${API_URL}/${id}`, data);
    return response.data;
  },

  // User accessibility methods
  async getUserAccessibleCopilots(): Promise<UserAccessibleCopilotsResponse> {
    const userId = getUserId();
    const workspaceId = getWorkspaceId();
    
    if (!userId || !workspaceId) {
      throw new Error('User authentication required');
    }

    const response = await HttpClient.apiClient.get(`${API_URL}/user/accessible`, {
      params: { userId, workspaceId }
    });
    return response.data;
  },

  async checkCopilotAccess(copilotId: string): Promise<CopilotAccessCheckResponse> {
    const userId = getUserId();
    const workspaceId = getWorkspaceId();
    
    if (!userId || !workspaceId) {
      throw new Error('User authentication required');
    }

    const response = await HttpClient.apiClient.get(`${API_URL}/${copilotId}/access-check`, {
      params: { userId, workspaceId }
    });
    return response.data;
  },

  async getUserCopilotPermissions(copilotId?: string): Promise<{
    data: {
      canView: boolean;
      canInteract: boolean;
      canManage: boolean;
      restrictedFeatures: string[];
      userTeams: any[];
    };
  }> {
    const userId = getUserId();
    const workspaceId = getWorkspaceId();

    if (!userId || !workspaceId) {
      throw new Error('User authentication required');
    }

    const params: any = { userId, workspaceId };
    if (copilotId) {
      params.copilotId = copilotId;
    }

    const response = await HttpClient.apiClient.get(`${API_URL}/user/permissions`, {
      params
    });
    return response.data;
  },

  // Initialize copilot with profile and tools context
  async initializeCopilot(conversationId?: string | null): Promise<CopilotInitializeResponse> {
    const mcpApiUrl = import.meta.env.VITE_MCP_API_URL || 'https://beta-ai.pullseai.com';
    const params = conversationId ? { conversation_id: conversationId } : {};

    const response = await HttpClient.apiClient.get(
      `${mcpApiUrl}/copilot/initialize`,
      { params }
    );
    return response.data;
  },

  // Create a new conversation
  async createConversation(data: CreateConversationRequest): Promise<CreateConversationResponse> {
    const mcpApiUrl = import.meta.env.VITE_MCP_API_URL || 'https://beta-ai.pullseai.com';

    const response = await HttpClient.apiClient.post(
      `${mcpApiUrl}/copilot/conversations`,
      data
    );
    return response.data;
  },

  async getConversationMessages(
    conversationId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<ConversationMessagesResponse> {
    const mcpApiUrl = import.meta.env.VITE_MCP_API_URL || 'https://beta-ai.pullseai.com';

    const response = await HttpClient.apiClient.get(
      `${mcpApiUrl}/copilot/conversations/${conversationId}/messages`,
      {
        params: {
          limit: params?.limit,
          offset: params?.offset,
        },
      }
    );

    return response.data;
  },

  async createStreamToken(conversationId: string): Promise<StreamTokenResponse> {
    if (!conversationId) {
      throw new Error('Conversation ID is required to request a stream token.');
    }

    const mcpApiUrl = import.meta.env.VITE_MCP_API_URL || 'https://beta-ai.pullseai.com';
    const response = await HttpClient.apiClient.post(`${mcpApiUrl}/copilot/chat/stream/token`, {
      conversation_id: conversationId,
    });

    return response.data;
  },
};
